import { useRef, useEffect, useCallback } from "react";
import { useSmsThread } from "./useSmsThread";
import { SmsComposer } from "./SmsComposer";
import { EmptyState } from "../shared/EmptyState";
import { RelativeTime } from "../shared/RelativeTime";
import { MessageSquare } from "lucide-react";

interface SmsThreadProps {
  contactId: string | undefined;
}

export function SmsThread({ contactId }: SmsThreadProps) {
  const { data, isLoading, error, refetch } = useSmsThread(contactId);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (data && data.length > 0) {
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [data]);

  const handleSent = useCallback(() => {
    refetch();
  }, [refetch]);

  if (isLoading) {
    return (
      <div style={{ padding: 24, textAlign: "center", color: "#9ca3af" }}>
        Loading messages…
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 24, color: "#dc2626" }}>
        Failed to load messages: {(error as Error).message}
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: 500,
        border: "1px solid #e5e7eb",
        borderRadius: 8,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "16px",
          backgroundColor: "#f9fafb",
        }}
      >
        {!data || data.length === 0 ? (
          <EmptyState
            icon={<MessageSquare size={32} />}
            title="No messages yet"
            description="Send the first message to start a conversation."
          />
        ) : (
          data.map((msg) => (
            <div
              key={msg.id}
              style={{
                display: "flex",
                justifyContent:
                  msg.direction === "outbound" ? "flex-end" : "flex-start",
                marginBottom: 12,
              }}
            >
              <div
                style={{
                  maxWidth: "70%",
                  padding: "8px 12px",
                  borderRadius: 12,
                  backgroundColor:
                    msg.direction === "outbound" ? "#2563eb" : "#ffffff",
                  color: msg.direction === "outbound" ? "#ffffff" : "#111827",
                  border:
                    msg.direction === "inbound"
                      ? "1px solid #e5e7eb"
                      : "none",
                  boxShadow:
                    msg.direction === "inbound"
                      ? "0 1px 2px rgba(0,0,0,0.05)"
                      : "none",
                }}
              >
                <div style={{ fontSize: 14, lineHeight: 1.5, whiteSpace: "pre-wrap" }}>
                  {msg.body}
                </div>
                <div
                  style={{
                    fontSize: 11,
                    marginTop: 4,
                    opacity: 0.7,
                    textAlign: "right",
                  }}
                >
                  <RelativeTime date={msg.created_at} />
                </div>
              </div>
            </div>
          ))
        )}
        <div ref={bottomRef} />
      </div>

      {contactId && (
        <SmsComposer contactId={contactId} onSent={handleSent} />
      )}
    </div>
  );
}
